import { isString } from 'lodash';
import { useState } from 'react';
import { Menu } from '@shared/components/Menu';
import {
  TagMultiSelectOptions,
  TagSingleSelectOptions,
} from '@shared/scenes/entity/components/fields/TagEditField';
import { TagComponent } from '@shared/scenes/workflows/config';
import { getComponentLabelWithFallback } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import styles from './TagComponentForm.module.css';

export interface TagComponentFormProps {
  /** The card's field JSON, as the collection view stores it. */
  field: unknown;
  fieldName: string;
  anchorName: string;
  loading: boolean;
  onClose(): void;
  onUpdateField(fieldName: string, value: any): void;
  component: TagComponent;
}

/**
 * The tag picker a collection view's input button opens. Mounted only while open: the menu opens
 * on mount, and closing it calls `onClose`. Picking a tag in a single-select list closes it.
 */
export function TagComponentForm({
  fieldName,
  field,
  onUpdateField,
  anchorName,
  component,
  onClose,
  loading,
}: TagComponentFormProps) {
  const options = component.options ?? [];
  const activeOptions = options.filter(option => !option.archived);
  // The card's raw field JSON: a tag field stores the selected values
  const tagIds = Array.isArray(field) ? field.filter(isString) : [];
  const selectedValues = options
    .filter(option => tagIds.includes(option.value))
    .map(option => option.value);
  const label = getComponentLabelWithFallback(component);

  const onChange = (newValues: string[]) => {
    onUpdateField(fieldName, newValues);
  };

  return (
    <Menu
      isOpen
      onOpenChange={isOpen => !isOpen && onClose()}
      anchorName={anchorName}
      align="center"
      closeOnSelect={false}
    >
      <Menu.Content mobileSheet className={styles.tagComponentFormMenu} aria-label={label}>
        <Menu.TitleBar title={label} endContent={<Menu.CloseButton />} mobileOnly />
        {loading && <Menu.Loading />}
        {!loading && (
          <TagOptions
            options={activeOptions}
            selectedValues={selectedValues}
            onChange={onChange}
            isMultiselect={component.multiselect ?? false}
            isRequired={component.required ?? false}
          />
        )}
      </Menu.Content>
    </Menu>
  );
}

function TagOptions({
  isMultiselect,
  ...props
}: {
  options: API.TagComponentOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  isMultiselect: boolean;
  isRequired: boolean;
}) {
  const [filter, setFilter] = useState('');
  const optionsProps = { ...props, filter, setFilter, canCreate: false };

  if (isMultiselect) {
    return <TagMultiSelectOptions {...optionsProps} selectAll={false} />;
  }
  return <TagSingleSelectOptions {...optionsProps} />;
}
